import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import PublicNav from '../components/PublicNav';
import Footer from '../components/Footer';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-100" data-testid="privacy-policy-page">
      <PublicNav currentPage="privacy-policy" />

      <div className="max-w-4xl mx-auto px-6 py-16">
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6 text-sm">
          <ArrowLeft className="mr-2" size={16} />
          Back to Home
        </Link>
        <h1 className="text-4xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
        <p className="text-slate-600 mb-8">Last updated: February 2026</p>

        <div className="bg-white border border-slate-200 shadow-sm p-8 rounded-lg space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">1. Introduction</h2>
            <p className="text-blue-800 leading-relaxed">
              SeedSynch ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal data when you use our platform. We operate in compliance with applicable data protection laws, including the UAE Federal Decree-Law No. 45 of 2021 on Personal Data Protection and the EU General Data Protection Regulation (GDPR).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">2. What Personal Data We Collect</h2>
            <p className="text-blue-800 leading-relaxed mb-4">
              We collect the following categories of personal data:
            </p>
            <ul className="list-disc list-inside text-blue-800 space-y-2 ml-4">
              <li><strong className="text-blue-900">Account Information:</strong> Name, email address, password (encrypted), and role (founder, investor, or co-founder)</li>
              <li><strong className="text-blue-900">Profile Information:</strong> Professional details such as company name, industry, location, funding requirements, investment criteria, skills, and experience</li>
              <li><strong className="text-blue-900">Communication Data:</strong> Messages exchanged with other users on the platform</li>
              <li><strong className="text-blue-900">Usage Data:</strong> Information about how you interact with the platform, including matches, likes, and NDA acceptances</li>
              <li><strong className="text-blue-900">Documents:</strong> Pitch decks and other files you choose to upload and share</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">3. Why We Collect Your Data</h2>
            <p className="text-blue-800 leading-relaxed mb-4">
              We process your personal data for the following purposes:
            </p>
            <ul className="list-disc list-inside text-blue-800 space-y-2 ml-4">
              <li><strong className="text-blue-900">Account Creation & Authentication:</strong> To create and manage your account and verify your identity</li>
              <li><strong className="text-blue-900">Matchmaking:</strong> To connect you with relevant founders, investors, or co-founders based on your criteria and preferences</li>
              <li><strong className="text-blue-900">Communication:</strong> To enable secure messaging between matched users</li>
              <li><strong className="text-blue-900">Platform Improvement:</strong> To understand how our platform is used and improve our services</li>
              <li><strong className="text-blue-900">Legal Compliance:</strong> To comply with applicable laws and protect our legal rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">4. Data Sharing</h2>
            <p className="text-blue-800 leading-relaxed mb-4">
              <strong className="text-blue-500">We do not sell your personal data to third parties.</strong>
            </p>
            <p className="text-blue-800 leading-relaxed mb-4">
              We may share your data only in the following limited circumstances:
            </p>
            <ul className="list-disc list-inside text-blue-800 space-y-2 ml-4">
              <li><strong className="text-blue-900">With Other Users:</strong> Your profile information is visible to other users for matchmaking purposes. Sensitive documents (like pitch decks) are only shared after NDA acceptance.</li>
              <li><strong className="text-blue-900">Service Providers:</strong> We may use trusted third-party services (e.g., hosting, analytics) that process data on our behalf under strict confidentiality agreements.</li>
              <li><strong className="text-blue-900">Legal Requirements:</strong> We may disclose data if required by law or to protect our rights and safety.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">5. Data Security</h2>
            <p className="text-blue-800 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. This includes encryption of sensitive data, secure authentication, and regular security assessments.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">6. Your Rights</h2>
            <p className="text-blue-800 leading-relaxed mb-4">
              You have the following rights regarding your personal data:
            </p>
            <ul className="list-disc list-inside text-blue-800 space-y-2 ml-4">
              <li><strong className="text-blue-900">Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong className="text-blue-900">Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong className="text-blue-900">Deletion:</strong> Request deletion of your personal data at any time</li>
              <li><strong className="text-blue-900">Data Portability:</strong> Request your data in a portable format</li>
              <li><strong className="text-blue-900">Withdraw Consent:</strong> Withdraw your consent to data processing at any time</li>
            </ul>
            <p className="text-blue-800 leading-relaxed mt-4">
              To exercise any of these rights, please contact us at <a href="mailto:hello@seedsynch.com" className="text-blue-500 hover:text-blue-300">hello@seedsynch.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">7. Data Retention</h2>
            <p className="text-blue-800 leading-relaxed">
              We retain your personal data for as long as your account is active or as needed to provide you with our services. If you request deletion of your account, we will delete your personal data within 30 days, except where we are required to retain certain information for legal or legitimate business purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">8. Changes to This Policy</h2>
            <p className="text-blue-800 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-blue-900 mb-4">9. Contact Us</h2>
            <p className="text-blue-800 leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="text-blue-800 mt-4">
              <strong className="text-blue-900">Email:</strong> <a href="mailto:hello@seedsynch.com" className="text-blue-500 hover:text-blue-300">hello@seedsynch.com</a>
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}
